import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useState, useCallback } from "react";

import { useVisita } from "../../../../../../context/VisitaContext";
import { Grid } from "@mui/material";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useStepSave } from "../../../../hooks/useStepSave";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import CardBox from "../../CardBox/CardBox";
import { SearchDiagnosticoCIE } from "../../../../../ResumenIngresoCreation/components/StepDatosGenerales/components/SearchDiagnosticoCIE";
import { shouldIncludeInput } from "../../../../utils/auxiliaryFunctions";
import { MultipleFileInput } from "../../../../../ResumenIngresoCreation/components/StepCuidadosPaliativos/components/MultipleFileInput";
import { GET_COMBO_VISITA_VALOR, adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import { GET_COMBO_RI, adaptCombosRIArray, adaptRadioCombosRIArray } from "../../../../../../data/graphQL/queries/ResumenIngreso/getSingleComboRI";
import toast from "react-hot-toast";
import { adaptVisitaCPDatosClinicosInput, SAVE_VISITA_CP_DATOS_CLINICOS } from '../../../../../../data/graphQL/mutations/Visitas/saveVisitaCP';
import { handleChangeSolvedFileVisita } from "../../../../utils/handleChangeImageVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import { EstudiosComplementarios } from "../../EstudiosComplementarios";
import { GET_ALL_ALERGIAS } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllAlergias";
import { adaptAlergiaArray } from "../../../../../../adapters/alergiaAdapter";
import CalculadoraESAS from "../../../../../ResumenIngresoCreation/components/StepCuidadosPaliativos/components/CalculadoraESAS";

const ESCALA_DOLOR_SI_ID = 342;
const ID_COMBO_ECOG = 6;

export const DatosClinicosCP = ({ readOnly, onSaveRef, onExposeFormatted }) => {
  const [formData, setFormData] = useState({});
  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const [childrenAction, setChildrenAction] = useState(null);
  const [ecogOptions, setEcogOptions] = useState([]);
  const [escalaLanskyOptions, setEscalaLanskyOptions] = useState([]);
  const [escalaDolorOptions, setEscalaDolorOptions] = useState([]);
  const [alergiasOptions, setAlergiasOptions] = useState([]);
  const { deleteArchivo } = useDeleteArchivoVisita();

  const [save] = useMutation(SAVE_VISITA_CP_DATOS_CLINICOS);

  const { data: dataEcog } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_ECOG }
  });

  useEffect(() => {
    if (!dataEcog) return;
    setEcogOptions(adaptCombosVisitas(dataEcog));
  }, [dataEcog]);

  const { data: dataLansky } = useQuery(GET_COMBO_RI, {
    variables: { campo: 'ESCALA DE LANSKY ' }
  });

  useEffect(() => {
    if (!dataLansky) return;
    setEscalaLanskyOptions(adaptCombosRIArray(dataLansky));
  }, [dataLansky]);

  const { data: dataEscalaDolor } = useQuery(GET_COMBO_RI, {
    variables: { campo: 'Escala de Dolor' }
  });

  useEffect(() => {
    if (!dataEscalaDolor) return;
    setEscalaDolorOptions(adaptRadioCombosRIArray(dataEscalaDolor));
  }, [dataEscalaDolor]);


  const { data: alergiasData } = useQuery(GET_ALL_ALERGIAS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!alergiasData) return;
    const newAlergias = adaptAlergiaArray(alergiasData);
    setAlergiasOptions(newAlergias);
  }, [alergiasData]);

  const alergiaOtrosId = useMemo(() => {
    const otrosId = alergiasOptions.find((x) => x.displayName.toLowerCase() === 'otros')?.id || -1;
    return otrosId;
  }, [alergiasOptions]);

  useEffect(() => {
    if (!alergiasOptions) return;

    const opcionNo = alergiasOptions.find((x) => x.displayName.includes('NO'));
    const disableCondition = formData.alergiasDatosClinicos?.includes(opcionNo?.id) ?
      (id) => id !== opcionNo?.id :
      (id) => formData.alergiasDatosClinicos?.length ? id === opcionNo?.id : false;

    setAlergiasOptions((prevAlergias) =>
      prevAlergias.map((ant) => ({ ...ant, disabled: disableCondition(ant.id) }))
    );
  }, [formData.alergiasDatosClinicos]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!formData?.eSAS) {
      handleChange("dolor", null);
      handleChange("disnea", null);
      handleChange("nauseas", null);
      handleChange("depresion", null);
      handleChange("ansiedad", null);
      handleChange("anorexia", null);
      handleChange("bienestar", null);
      handleChange("cansancio", null);
      handleChange("insomnio", null);
      handleChange("somnolencia", null);
      handleChange("constipacion", null);
      handleChange("resultadoESAS", null);
    }
  }, [formData?.eSAS]);

  const formattedInputs = useMemo(() => {
    return inputsGroups.
      filter((group) => shouldIncludeInput(group, visita.edad)).
      map((group) => ({
        ...group,
        inputs: group.inputs.
          map((input) => {
            const newInput = { ...input };

            if (newInput.name === "idComboRIValorECOG") newInput.options = ecogOptions;

            if (newInput.name === "idComboRIValorLANSKY") newInput.options = escalaLanskyOptions;

            if (newInput.name === 'idComboRIValorEscalaDolor') newInput.options = escalaDolorOptions;

            if (newInput.name === 'resultadoFLACCR') {
              newInput.required = !!formData.fLACCR;
              newInput.hidden = !formData.fLACCR;
            }

            if (newInput.name === 'resultadoGustaveRoussy') {
              newInput.required = !!formData.gustaveRoussy;
              newInput.hidden = !formData.gustaveRoussy;
            }

            if (newInput.name === 'resultadoFPSR') {
              newInput.required = !!formData.pedFPSR;
              newInput.hidden = !formData.pedFPSR;
            }

            if (newInput.name === 'resultadoEscalaNumericaDolor') {
              newInput.required = !!formData.escalaNumericaDolor;
              newInput.hidden = !formData.escalaNumericaDolor;
            }

            if (newInput.name === 'eSAS') {
              newInput.hidden = !formData.enfermedadOncologica;
            }

            if (newInput.name === 'resultadoESAS') {
              newInput.hidden = !formData.enfermedadOncologica;
              newInput.required = !!formData.eSAS;
              newInput.disabled = !formData.eSAS;
            }

            if (newInput.name === 'alergiasDatosClinicos') {
              newInput.options = alergiasOptions;
            }

            if (newInput.name === 'descripcionAlergiaOtro') {
              const hasOtrasAlergias = formData.alergiasDatosClinicos?.includes(alergiaOtrosId);
              newInput.hidden = !hasOtrasAlergias;
              newInput.required = !!hasOtrasAlergias;
            }

            if (readOnly) newInput.disabled = true;

            return newInput;
          })
      }));
  }, [
    readOnly,
    visita.edad,
    formData.eSAS,
    formData.enfermedadOncologica,
    formData.pedFPSR,
    formData.fLACCR,
    formData.gustaveRoussy,
    formData.escalaNumericaDolor,
    ecogOptions,
    escalaLanskyOptions,
    escalaDolorOptions,
    alergiasOptions,
    formData.alergiasDatosClinicos]
  );

  const handleChangeFile = useCallback((name, file) => {
    handleChangeSolvedFileVisita({
      saveArchivoVisita,
      name,
      fileValue: file,
      idVisita: visita.idVisita,
      handleChange,
      archivos: formData?.estudioAdjunto,
      idTipoArchivo: tipoArchivoVisita.ESTUDIO_COMPLEMENTARIO
    });
  }, [visita.idVisita, saveArchivoVisita, formData?.estudioAdjunto]);

  const onDeleteImage = useCallback((name, idArchivo, archivos) => {
    deleteArchivo({
      idArchivo: idArchivo,
      name: name,
      archivos: archivos,
      handleChange
    });
  }, [visita.idVisita]);

  useStepSave(onSaveRef, async (validate) => {
    if (formData.idComboRIValorEscalaDolor === ESCALA_DOLOR_SI_ID &&
      !formData.fLACCR &&
      !formData.gustaveRoussy &&
      !formData.pedFPSR &&
      !formData.escalaNumericaDolor) {
      return toast.error('Debe completar el menos una de las escalas.');
    }
    if (validate) validateStepFields({ formattedGroups: formattedInputs, formData });

    const { data } = await save({
      variables: { input: adaptVisitaCPDatosClinicosInput(formData, alergiaOtrosId) }
    });

    const idVisita = data?.saveVisitaCPDatosClinicos?.idVisita;
    if (!idVisita) throw new Error("Error al guardar");

    setVisita((prev) => ({ ...prev, ...formData }));
  });

  useEffect(() => {
    setFormData(visita);
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "datosClinicosCP",
      stepName: "Datos Clínicos",
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  useEffect(() => {
    if (formData.idComboRIValorEscalaDolor !== ESCALA_DOLOR_SI_ID) {
      setFormData((prevDatos) => ({
        ...prevDatos,
        fLACCR: false,
        resultadoFLACCR: null,
        gustaveRoussy: false,
        resultadoGustaveRoussy: null,
        pedFPSR: false,
        resultadoFPSR: null,
        escalaNumericaDolor: false,
        resultadoEscalaNumericaDolor: null,
        eSAS: false
      }));
    }
  }, [formData.idComboRIValorDolor]);

  return (
    <CardBox title="Datos clínicos">

      {formattedInputs
        .filter((x) => x.inputs.some((input) => !input.hidden))
        .map((group) =>
          <Grid className='step-form-section-container' container spacing={2}>
            {
              group.groupName === 'Estudios complementarios' ?
                <label className={`step-form-section-title ${childrenAction ? 'with-children-action' : ''}`}>
                  {group.groupName} {Boolean(childrenAction) && childrenAction}
                </label> :

                <label className='step-form-section-title'>{group.groupName}</label>
            }
            {group.inputs.filter((input) => !input.hidden).
              map((input) =>
                <Grid size={{ xs: 12 }} key={input.name}>
                  {!['listaDiagnostico', 'estudioAdjunto', 'resultadoESAS'].includes(input.name) && <RenderInputByTypeMobile
                    input={input}
                    label={input.displayName}
                    value={formData[input.name]}
                    handleChange={handleChange}
                    key={`${input.name}-${input.type}${input.type === 'multiple_boolean' ? formData[input.name] : ''}`}
                    row={input.type === "multiple_boolean"} />
                  }
                  {input.name === 'listaDiagnostico' && <SearchDiagnosticoCIE
                    diagnosticosRIModel={formData.listaDiagnostico}
                    handleChange={handleChange}
                    inputName="listaDiagnostico"
                    readOnly={readOnly} />
                  }
                  {input.name === 'estudioAdjunto' && <EstudiosComplementarios
                    key={`${group.groupName}-${input.name}`}
                    name={input.name}
                    handleChange={handleChangeFile}
                    setChildrenAction={setChildrenAction}
                    prevAdjuntos={formData[input.name]}
                    onDeleteAdjunto={(name, idArchivo, archivos) => onDeleteImage(name, idArchivo, archivos)}
                    readOnly={readOnly} />
                  }
                  {input.name === 'resultadoESAS' && (
                    <CalculadoraESAS
                      variant="esasVisita"
                      handleChange={handleChange}
                      input={input}
                      data={formData}
                      prevResult={formData[input.name]}
                      disabledModal={input.disabled}
                    />
                  )}
                </Grid>
              )}
          </Grid>
        )}
    </CardBox>);

};

const inputsGroups = [
  {
    groupName: '',
    inputs: [
      {
        name: "listaDiagnostico",
        displayName: "Diagnóstico CIE-10",
        type: "custom",
        required: true
      },
      {
        name: "enfermedadOncologica",
        displayName: "Enf. que motiva el soporte es oncológico",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        displayName: 'Alergias Relevantes',
        name: 'alergiasDatosClinicos',
        type: 'multiple_select',
        options: [],
        required: true
      },
      {
        displayName: 'Otras alergias',
        name: 'descripcionAlergiaOtro',
        type: 'long_string',
        required: true
      },
      {
        name: "soporteNutricional",
        displayName: "Requiere soporte nutricional",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "medicoCabecera",
        displayName: "Médico de cabecera",
        type: "string",
        lightTitle: true,
        required: true
      }]

  },
  {
    groupName: 'Estudios complementarios',
    inputs: [
      {
        displayName: 'Estudio complementario',
        name: 'estudioAdjunto'
      }]

  },
  {
    groupName: '',
    inputs: [
      {
        name: "idComboRIValorECOG",
        displayName: "ECOG",
        type: "select",
        required: true
      },
      {
        name: "examenFisico",
        displayName: "Examen físico(Datos positivos)",
        type: "long_string",
        required: true
      },
      {
        name: "sondaVesical",
        displayName: "Sonda vesical",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "cateter",
        displayName: "Catéter",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "sondaNasogastrica",
        displayName: "Sonda nasogástrica",
        type: "boolean",
        lightTitle: true,
        required: true
      }]

  },
  {
    groupName: 'Solo Adultos',
    esAdulto: true,
    inputs: [
      {
        displayName: "ESAS",
        name: "eSAS",
        type: "multiple_boolean",
        options: [
          { value: true, label: "SI" },
          { value: false, label: "NO Corresponde" }],

        required: true,
        row: true,
        lightTitle: true
      },
      {
        displayName: "Resultado ESAS",
        name: "resultadoESAS",
        type: "float",
        required: true
      }]

  },
  {
    groupName: 'Solo Pediátricos',
    esPediatrico: true,
    inputs: [
      {
        name: "idComboRIValorLANSKY",
        displayName: "Escala LANSKY",
        type: "select",
        required: true
      },
      {
        name: "idComboRIValorEscalaDolor",
        displayName: "Escala de dolor",
        type: "multiple_boolean",
        required: true,
        row: true,
        lightTitle: true
      },
      {
        name: "fLACCR",
        displayName: "Escala FLACC-R",
        type: "multiple_boolean",
        required: true,
        options: [
          { value: true, label: 'Si' },
          { value: false, label: 'No corresponde' }],

        row: true,
        lightTitle: true
      },
      {
        name: "resultadoFLACCR",
        displayName: "Resultado",
        type: "int",
        min: 0,
        max: 10,
        required: false
      },
      {
        name: "gustaveRoussy",
        displayName: "Escala Gustave-Roussy",
        type: "multiple_boolean",
        required: true,
        options: [
          { value: true, label: 'Si' },
          { value: false, label: 'No corresponde' }],

        row: true,
        lightTitle: true
      },
      {
        name: "resultadoGustaveRoussy",
        displayName: "Resultado",
        type: "int",
        min: 0,
        max: 10,
        required: false
      },
      {
        name: "pedFPSR",
        displayName: "Escala FPS-R",
        type: "multiple_boolean",
        required: true,
        options: [
          { value: true, label: 'Si' },
          { value: false, label: 'No corresponde' }],

        row: true,
        lightTitle: true
      },
      {
        name: "resultadoFPSR",
        displayName: "Resultado",
        type: "select",
        options: [
          { id: 0, displayName: "0" },
          { id: 2, displayName: "2" },
          { id: 4, displayName: "4" },
          { id: 6, displayName: "6" },
          { id: 8, displayName: "8" },
          { id: 10, displayName: "10" }],

        required: false
      },
      {
        name: "escalaNumericaDolor",
        displayName: "Escala Numérica de dolor",
        type: "multiple_boolean",
        required: true,
        options: [
          { value: true, label: 'Si' },
          { value: false, label: 'No corresponde' }],

        row: true,
        lightTitle: true
      },
      {
        name: "resultadoEscalaNumericaDolor",
        displayName: "Resultado",
        type: "int",
        required: false
      }]

  }];